import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, LogOut, Calendar, ShieldCheck, Pencil, Check } from "lucide-react";
import Avatar from "./Avatar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const DetailRow = ({ icon: Icon, label, value, darkMode }) => (
  <div className="flex items-center gap-3 px-4 py-3">
    <div
      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border ${darkMode
          ? "border-white/[0.08] bg-white/[0.03] text-accent-200"
          : "border-sky-200 bg-sky-50 text-sky-600"
        }`}
    >
      <Icon className="h-4 w-4" />
    </div>
    <div className="min-w-0 flex-1">
      <p className={`text-[11px] font-medium uppercase tracking-wide ${darkMode ? "text-slate-500" : "text-slate-400"}`}>
        {label}
      </p>
      <p className={`truncate text-sm ${darkMode ? "text-slate-100" : "text-slate-800"}`}>{value}</p>
    </div>
  </div>
);

const ProfileModal = ({ open, onClose, user, onSignOut, darkMode = true }) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [newName, setNewName] = useState("");
  const [nameLoading, setNameLoading] = useState(false);
  const [nameError, setNameError] = useState("");

  const name = user?.name || "User";
  const email = user?.email || "—";

  useEffect(() => {
    if (open) {
      setNewName(name);
      setIsEditingName(false);
      setNameError("");
    }
  }, [open, name]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const handleSaveName = async () => {
    const trimmed = newName.trim();
    if (!trimmed || trimmed === name) {
      setIsEditingName(false);
      return;
    }
    setNameLoading(true);
    setNameError("");

    try {
      const params = new URLSearchParams();
      params.append("new_name", trimmed);

      const response = await fetch(`${BACKEND_URL}/api/auth/update-name`, {
        method: "POST",
        body: params,
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        credentials: "include",
      });

      const data = await response.json();
      if (response.ok) {
        // Update local storage and force a reload to get the new name in Avatar/Chat, or just update the object.
        // Since we passed `user` as a prop, modifying the object directly is a quick hack to update it across the UI if it's referenced.
        // A better way is to update localStorage and reload, or trigger a state update in the parent.
        if (user) user.name = trimmed;
        const localUser = JSON.parse(localStorage.getItem("user") || "{}");
        if (localUser) {
          localUser.name = trimmed;
          localStorage.setItem("user", JSON.stringify(localUser));
        }
        setIsEditingName(false);

        // Dispatch a custom event so parent components (like Avatar/Sidebar) can update
        window.dispatchEvent(new Event("user-updated"));
      } else {
        setNameError(data.detail || "Failed to update username");
      }
    } catch (err) {
      setNameError("Server error");
    } finally {
      setNameLoading(false);
    }
  };

  const memberSince = user?.created_at
    ? new Date(user.created_at).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
    })
    : null;

  // Prefer the explicit provider from the backend; fall back to inferring
  // Google from the presence of a profile photo.
  const provider = user?.auth_provider || (user?.picture ? "google" : null);
  const providerLabel =
    provider === "google"
      ? "Google"
      : provider === "email"
        ? "Email & password"
        : null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          key="profile-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 z-[120] flex items-center justify-center bg-ink-950/80 p-4 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            key="profile-panel"
            role="dialog"
            aria-modal="true"
            initial={{ opacity: 0, y: 16, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.97 }}
            transition={{ type: "spring", stiffness: 300, damping: 26 }}
            onClick={(e) => e.stopPropagation()}
            className={`relative w-full max-w-md overflow-hidden rounded-3xl shadow-2xl ${darkMode ? "glass-strong" : "bg-white border border-slate-200"
              }`}
            style={darkMode ? { background: "#0A1226" } : undefined}
          >
            <button
              onClick={onClose}
              className={`absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${darkMode
                  ? "text-slate-500 hover:bg-white/[0.06] hover:text-slate-200"
                  : "text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                }`}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>

            {/* Header */}
            <div className="flex items-center gap-4 px-6 pb-5 pt-7">
              <Avatar
                user={user}
                className="h-16 w-16 shrink-0 rounded-2xl text-2xl shadow-glow-sm"
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 group">
                  {isEditingName ? (
                    <div className="flex flex-col gap-1 w-full">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={newName}
                          onChange={(e) => setNewName(e.target.value)}
                          className={`flex-1 rounded-lg px-2 py-1 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-sky-500/50 border ${darkMode ? "bg-ink-900 border-white/10 text-white" : "bg-white border-slate-200 text-slate-900"}`}
                          disabled={nameLoading}
                          autoFocus
                        />
                        <button
                          onClick={handleSaveName}
                          disabled={nameLoading}
                          className={`flex h-7 w-7 items-center justify-center rounded-lg transition-colors ${darkMode ? "bg-sky-500/20 text-sky-400 hover:bg-sky-500/30" : "bg-sky-100 text-sky-600 hover:bg-sky-200"}`}
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => { setIsEditingName(false); setNewName(name); setNameError(""); }}
                          disabled={nameLoading}
                          className={`flex h-7 w-7 items-center justify-center rounded-lg transition-colors ${darkMode ? "bg-white/10 text-slate-400 hover:bg-white/20 hover:text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700"}`}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                      {nameError && <p className="text-[10px] text-red-500">{nameError}</p>}
                    </div>
                  ) : (
                    <>
                      <h3 className={`truncate text-lg font-semibold ${darkMode ? "text-white" : "text-slate-900"}`}>
                        {name}
                      </h3>
                      <button
                        onClick={() => setIsEditingName(true)}
                        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100 ${darkMode ? "text-slate-400 hover:bg-white/10 hover:text-white" : "text-slate-400 hover:bg-slate-100 hover:text-slate-700"}`}
                        aria-label="Edit name"
                      >
                        <Pencil className="h-3 w-3" />
                      </button>
                    </>
                  )}
                </div>
                <p className={`truncate text-sm ${darkMode ? "text-slate-400" : "text-slate-500"}`}>{email}</p>
              </div>
            </div>

            {/* Details */}
            {(memberSince || providerLabel) && (
              <div
                className={`mx-4 rounded-2xl border ${darkMode
                    ? "divide-y divide-white/[0.06] border-white/[0.06] bg-white/[0.02]"
                    : "divide-y divide-slate-200 border-slate-200 bg-slate-50"
                  }`}
              >
                {memberSince && (
                  <DetailRow icon={Calendar} label="Member since" value={memberSince} darkMode={darkMode} />
                )}
                {providerLabel && (
                  <DetailRow icon={ShieldCheck} label="Sign-in method" value={providerLabel} darkMode={darkMode} />
                )}
              </div>
            )}



            {/* Footer */}
            <div className="flex items-center justify-between gap-3 px-6 py-5">
              <button
                onClick={onSignOut}
                className={`flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-medium transition-colors ${darkMode
                    ? "border-red-500/25 text-red-300 hover:bg-red-500/10"
                    : "border-red-300 text-red-600 hover:bg-red-50"
                  }`}
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </button>
              <button
                onClick={onClose}
                className="rounded-xl bg-accent-gradient px-4 py-2 text-sm font-semibold text-[#04121F] shadow-glow-sm transition-all hover:brightness-110"
              >
                Done
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default ProfileModal;
